import React from 'react'
import Drag_icon from '../../../assets/images/drag-icon'

/**
 * Floating, draggable shell for the classic requestor search grid.
 *
 * `centerOnOpen` (AB#35147) opens the popup centred in the viewport instead of anchored
 * where the parent puts it. The Quick Entry requestor host needs that: classic anchors
 * this grid to the data-entry pane, and in that host the pane is visibility:hidden, so
 * an unanchored popup renders stranded in a corner. The centring lives HERE, in the same
 * inline style the drag writes, rather than in the host's stylesheet - the host used to
 * pin it with `position/top/left: !important`, which beats an inline style, so the drag
 * updated state on every mousemove while the rendered position never moved. Expressed as
 * a starting position, it is something the drag can simply overwrite.
 *
 * Percentages + a transform rather than measured pixels: exact at any size, and immune
 * to the grid's rows arriving after mount. The first mousedown switches to pixel
 * coordinates and drops the transform, so the two never fight.
 */
export default function DragTableWrapper({requestorTable, setGrid, centerOnOpen = false, children}) {

    const [tablePosition, setTablePosition] = React.useState<{top?: any, left: any}>({top: 'auto', left: 0});
    const [dragActive, setDragActive] = React.useState(false);

    const moveTable = React.useCallback((e) => {
        const targetWidth = e.target.offsetWidth
        const mousePositionX = e.clientX
        let requestorTableWidth = e.target.classList.contains('draggable-table-header') ?
            targetWidth : document.getElementById('draggable-requestor-table')?.offsetWidth
        setTablePosition({left: mousePositionX - requestorTableWidth/2, top: e.clientY - 18})
    }, [])

    const activateDrag = (e) => {
        const targetWidth = e.target.offsetWidth;
        const mousePositionX = e.clientX;
        setDragActive(true);
        setTablePosition({left: mousePositionX - targetWidth/2, top: e.clientY - 16})
        e.preventDefault()
        document.addEventListener('mousemove', moveTable);
    }

    const deactivateDrag = React.useCallback(() => {
        document.removeEventListener('mousemove', moveTable);
    }, [moveTable])

    // AB#35147: mouseup on the DOCUMENT, not on the container it used to be bound to.
    // A drag routinely ends with the cursor outside the popup, and the container's own
    // handler never fires then - the mousemove listener survives and the grid follows the
    // cursor indefinitely. Harmless while the popup could not move at all; a real bug the
    // moment it can. The unmount cleanup also drops a listener that used to leak.
    React.useEffect(() => {
        document.addEventListener('mouseup', deactivateDrag);
        return () => {
            document.removeEventListener('mouseup', deactivateDrag);
            document.removeEventListener('mousemove', moveTable);
        };
    }, [deactivateDrag, moveTable]);

    // Centred only until the specialist grabs it; from then on the drag's own coordinates win.
    const centered = centerOnOpen && !dragActive;

    if (requestorTable){
        return (
            <div className='draggable-table-container'
            id='draggable-requestor-table'
            style={{
                top: centered ? '50%' : tablePosition.top,
                left: centered ? '50%' : tablePosition.left,
                position: centered || dragActive ? 'fixed' : 'absolute',
                transform: centered ? 'translate(-50%, -50%)' : undefined,
            }}
            >
                <div className='draggable-table-header'
                onMouseDown={activateDrag}
                >
                    <span className='grabbable-area' >
                        <Drag_icon />
                    </span>
                    <span className='close-icon alert-close-icon close-icon-container'
                    onMouseDown={(e)=>{e.stopPropagation()}}
                    onClick={(e) => setGrid(false)}
                    ></span>
                </div>
                {children}
            </div>
        )
    }
    else return <>{children}</>;
}
